import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, GitBranch, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  fetchAssignmentRoutings,
  createAssignmentRoutingApi,
  updateAssignmentRoutingApi,
  deleteAssignmentRoutingApi,
} from '@/hooks/useAdmin';
import { fetchOperators, fetchInternalUsers } from '@/hooks/useTickets';
import type {
  AssignmentRoutingItem,
  CreateAssignmentRoutingRequest,
  UpdateAssignmentRoutingRequest,
  OperatorListItem,
  InternalUserItem,
} from '@uniticket/shared';

const TICKET_TYPE_VALUES = ['OPEN_ORDER', 'ADDRESS_MANAGEMENT', 'SUPPORT_REQUEST'] as const;

export function AssignmentRoutingPage() {
  const { t } = useTranslation(['admin', 'common']);
  const [routings, setRoutings] = useState<AssignmentRoutingItem[]>([]);
  const [operators, setOperators] = useState<OperatorListItem[]>([]);
  const [internalUsers, setInternalUsers] = useState<InternalUserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRouting, setEditingRouting] = useState<AssignmentRoutingItem | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      const [routingRes, opRes, usersRes] = await Promise.all([
        fetchAssignmentRoutings(),
        fetchOperators(),
        fetchInternalUsers(),
      ]);
      setRoutings(routingRes.data);
      setOperators(opRes.data);
      setInternalUsers(usersRes.data);
    } catch (err: any) {
      toast.error(err.message || t('admin:assignmentRouting.loadError'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  function handleAdd() {
    setEditingRouting(null);
    setDialogOpen(true);
  }

  function handleEdit(routing: AssignmentRoutingItem) {
    setEditingRouting(routing);
    setDialogOpen(true);
  }

  async function handleDelete(id: string) {
    try {
      await deleteAssignmentRoutingApi(id);
      toast.success(t('admin:assignmentRouting.deleted'));
      setDeleteConfirm(null);
      loadData();
    } catch (err: any) {
      toast.error(err.message || t('admin:assignmentRouting.deleteError'));
    }
  }

  async function handleSave(data: CreateAssignmentRoutingRequest | UpdateAssignmentRoutingRequest) {
    try {
      if (editingRouting) {
        await updateAssignmentRoutingApi(editingRouting.id, data as UpdateAssignmentRoutingRequest);
        toast.success(t('admin:assignmentRouting.updated'));
      } else {
        await createAssignmentRoutingApi(data as CreateAssignmentRoutingRequest);
        toast.success(t('admin:assignmentRouting.created'));
      }
      setDialogOpen(false);
      loadData();
    } catch (err: any) {
      toast.error(err.message || t('admin:assignmentRouting.saveError'));
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
            <GitBranch className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800">{t('admin:assignmentRouting.title')}</h2>
            <p className="text-sm text-slate-500">{t('admin:assignmentRouting.count', { count: routings.length })}</p>
          </div>
        </div>
        <Button onClick={handleAdd} className="bg-brand-gradient text-white hover:opacity-90">
          <Plus className="w-4 h-4 mr-2" />
          {t('admin:assignmentRouting.add')}
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('admin:assignmentRouting.cols.rule')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('admin:assignmentRouting.cols.operator')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('admin:assignmentRouting.cols.ticketType')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('admin:assignmentRouting.cols.assignedTo')}</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('admin:assignmentRouting.cols.status')}</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('admin:assignmentRouting.cols.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {routings.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                    {t('admin:assignmentRouting.empty')}
                  </td>
                </tr>
              ) : (
                routings.map((routing) => (
                  <tr key={routing.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="font-medium text-slate-700 text-xs">
                          {routing.operatorName ?? t('admin:assignmentRouting.defaultAll')} / {t(`common:ticketType.${routing.ticketType}`)}
                        </span>
                        {routing.label && (
                          <span className="text-xs text-brand-rose">{routing.label}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-700">
                      {routing.operatorName ?? t('admin:assignmentRouting.defaultAll')}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {t(`common:ticketType.${routing.ticketType}`)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="font-medium text-slate-700">{routing.assignToUserName}</span>
                        <span className="text-xs text-slate-400">{routing.assignToUserEmail}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {routing.isActive ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                          {t('admin:assignmentRouting.active')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                          {t('admin:assignmentRouting.inactive')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleEdit(routing)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-brand-rose hover:bg-brand-rose/5 transition-colors"
                          title={t('common:actions.edit')}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        {deleteConfirm === routing.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDelete(routing.id)}
                              className="px-2 py-1 text-xs font-semibold bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                            >
                              {t('common:actions.confirm')}
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="p-1 text-slate-400 hover:text-slate-600"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(routing.id)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            title={t('common:actions.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog */}
      {dialogOpen && (
        <AssignmentRoutingDialog
          editing={editingRouting}
          operators={operators}
          internalUsers={internalUsers}
          onSave={handleSave}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </div>
  );
}

// ── Assignment Routing Dialog ────────────────────────────

interface AssignmentRoutingDialogProps {
  editing: AssignmentRoutingItem | null;
  operators: OperatorListItem[];
  internalUsers: InternalUserItem[];
  onSave: (data: CreateAssignmentRoutingRequest | UpdateAssignmentRoutingRequest) => Promise<void>;
  onClose: () => void;
}

const NO_OPERATOR = '__default__';

function AssignmentRoutingDialog({ editing, operators, internalUsers, onSave, onClose }: AssignmentRoutingDialogProps) {
  const { t } = useTranslation(['admin', 'common']);
  const [operatorId, setOperatorId] = useState(editing?.operatorId ?? NO_OPERATOR);
  const [ticketType, setTicketType] = useState(editing?.ticketType || '');
  const [assignToUserId, setAssignToUserId] = useState(editing?.assignToUserId || '');
  const [customLabel, setCustomLabel] = useState(editing?.label || '');
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const resolvedOperatorId = operatorId === NO_OPERATOR ? null : operatorId;
      const labelValue = customLabel.trim() || null;

      if (editing) {
        await onSave({
          assignToUserId: assignToUserId || undefined,
          label: labelValue,
          isActive,
        } satisfies UpdateAssignmentRoutingRequest);
      } else {
        if (!ticketType || !assignToUserId) {
          toast.error(t('common:validation.required'));
          return;
        }
        await onSave({
          operatorId: resolvedOperatorId,
          ticketType,
          assignToUserId,
          label: labelValue,
        } satisfies CreateAssignmentRoutingRequest);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="gradient-band px-6 py-4 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white uppercase tracking-wide">
            {editing ? t('admin:assignmentRouting.editTitle') : t('admin:assignmentRouting.createTitle')}
          </h3>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[calc(100vh-8rem)] overflow-y-auto">
          {/* Operator selector */}
          {!editing ? (
            <div className="space-y-1.5">
              <Label>{t('admin:assignmentRouting.cols.operator')}</Label>
              <Select value={operatorId} onValueChange={setOperatorId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('admin:assignmentRouting.selectOperator')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_OPERATOR}>{t('admin:assignmentRouting.defaultAll')}</SelectItem>
                  {operators.map((op) => (
                    <SelectItem key={op.id} value={op.id}>{op.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">{t('admin:assignmentRouting.operatorOverride')}</p>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 rounded-lg px-4 py-2.5">
              <span className="font-semibold text-slate-700">{editing.operatorName ?? t('admin:assignmentRouting.defaultAll')}</span>
              <span>&middot;</span>
              <span>{t(`common:ticketType.${editing.ticketType}`)}</span>
            </div>
          )}

          {/* Ticket Type (only for new) */}
          {!editing && (
            <div className="space-y-1.5">
              <Label>{t('admin:assignmentRouting.cols.ticketType')}</Label>
              <Select value={ticketType} onValueChange={setTicketType}>
                <SelectTrigger>
                  <SelectValue placeholder={t('admin:assignmentRouting.selectType')} />
                </SelectTrigger>
                <SelectContent>
                  {TICKET_TYPE_VALUES.map((tv) => (
                    <SelectItem key={tv} value={tv}>{t(`common:ticketType.${tv}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Assign to user */}
          <div className="space-y-1.5">
            <Label>{t('admin:assignmentRouting.cols.assignedTo')}</Label>
            <Select value={assignToUserId} onValueChange={setAssignToUserId}>
              <SelectTrigger>
                <SelectValue placeholder={t('admin:assignmentRouting.selectUser')} />
              </SelectTrigger>
              <SelectContent>
                {internalUsers.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.displayName} ({u.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400">{t('admin:assignmentRouting.userHint')}</p>
          </div>

          {/* Custom label */}
          <div className="space-y-1.5">
            <Label>{t('admin:assignmentRouting.customLabel')} <span className="text-slate-400 font-normal">{t('admin:assignmentRouting.optional')}</span></Label>
            <Input
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
              placeholder={t('admin:assignmentRouting.labelPlaceholder')}
              maxLength={150}
            />
          </div>

          {/* Active toggle (edit only) */}
          {editing && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setIsActive(!isActive)}
                className={`relative w-11 h-6 rounded-full transition-colors ${isActive ? 'bg-green-500' : 'bg-slate-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isActive ? 'translate-x-5' : ''}`} />
              </button>
              <Label className="cursor-pointer" onClick={() => setIsActive(!isActive)}>
                {isActive ? t('admin:assignmentRouting.active') : t('admin:assignmentRouting.inactive')}
              </Label>
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" disabled={saving} className="bg-brand-gradient text-white hover:opacity-90">
              {saving ? t('admin:assignmentRouting.saving') : editing ? t('admin:assignmentRouting.updateRule') : t('admin:assignmentRouting.createRule')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
